<script lang="ts">
	import { getContext, setContext } from "svelte";
	import { readable, writable, type Readable, type Writable } from "svelte/store";
	import { type BrandKit } from "#gen/brands/v1/service_pb.js";
	import type { AssetRef, EntryPoint, Module } from "#gen/bundles/v1/bundle_pb.js";
	import type { GetDomainPublicConfigResponse } from "#gen/links/v1/links_pb.js";
	import type { AssetLoad } from "#lib/api/bundles.js";
	import type { AssetManager } from "#lib/assetManager.js";
	import SuiteViewer from "#lib/components/flow/viewer/SuiteViewer.svelte";
	import { parseDagFromSuite, type BundleDag } from "#lib/dag.js";
	import { createResolutionsStore } from "#lib/passthrough.js";
	import { createDesktopStore } from "#lib/stores/breakpoints.js";
	import { createDataItem } from "#lib/stores/suite.js";
	import { PUBLIC_ONE_TRUST_CONVERSIONS_DOMAIN_ID } from "$app/env/public";
	import type { PageData } from "./$types";

	export let data: PageData;

	const assetManager: Writable<AssetManager> = writable(data.assetManager);
	const brandKit: Writable<BrandKit | undefined> = writable(data.brandKit);
	const entryPoint: Writable<EntryPoint> = writable(data.entryPoint);
	const modules: Writable<Record<string, Module>> = writable(data.modules);
	const passthroughResolutions = createResolutionsStore(data.resolutions);
	const dag: Writable<BundleDag<AssetRef>> = writable(parseDagFromSuite(data.suite, data.modules));
	const assets: Writable<Record<string, AssetLoad>> = writable(data.assets);
	const suite = writable(data.suite);
	const oneTrustId = data?.dpaDomainInfo?.oneTrustId || PUBLIC_ONE_TRUST_CONVERSIONS_DOMAIN_ID;
	const dpaDomainInfo: Readable<GetDomainPublicConfigResponse | undefined> = readable(data?.dpaDomainInfo);
	const isDesktopUserAgent = getContext<Readable<boolean>>("isDesktop");
	const isDesktop = createDesktopStore($isDesktopUserAgent);

	$: assetManager.set(data.assetManager);
	$: suite.set(data.suite);
	$: brandKit.set(data.brandKit);
	$: entryPoint.set(data.entryPoint);
	$: assets.set(data.assets);
	$: passthroughResolutions.replace(data.resolutions);
	$: modules.set(data.modules);
	$: dag.set(parseDagFromSuite(data.suite, data.modules));
	$: suiteData = createDataItem(data.suite, data.assetLoad);
	$: desktopViewEnabled = $suite.desktopViewEnabled;

	setContext("assetManager", assetManager);
	setContext("cookie", data.cookie);
	setContext("suite", suite);
	setContext("registry", data.registry);
	setContext("entryPoint", entryPoint);
	setContext("brandKit", brandKit);
	setContext("assets", assets);
	setContext("passthroughResolutions", passthroughResolutions);
	setContext("modules", modules);
	setContext("dag", dag);
	setContext("dpaDomainInfo", dpaDomainInfo);

	function onLoaded(asset: AssetLoad) {
		$assets[asset.key()] = asset;
	}
</script>

<svelte:head>
	<title>{$suite.name}</title>
	<meta name="type" property="og:type" content="website" />
	<meta name="title" property="og:title" content={$suite.name} />
	<meta name="description" property="og:description" content={$suite.description} />
	{#if suiteData.images.length > 0}
		<meta name="image" property="og:image" content={suiteData.images[0].url} />
	{/if}
	{#if !!oneTrustId}
		<script src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js" type="text/javascript" charset="UTF-8" data-domain-script={oneTrustId} async></script>
	{/if}
	{#if data.customCodeHead}
		<!-- eslint-disable-next-line svelte/no-at-html-tags -->
		{@html data.customCodeHead}
	{/if}
</svelte:head>

<SuiteViewer initialAsset={data.assetLoad} {onLoaded} isDesktop={$isDesktop && desktopViewEnabled} />
